import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { formatDistanceToNowStrict, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { RxChevronLeft } from 'react-icons/rx';
import {
  FiMoreVertical,
  FiCheckCircle,
  FiTrash2,
  FiInfo,
  FiX,
} from 'react-icons/fi';
import classNames from 'classnames';
import { ReactSVG } from 'react-svg';
import toast from 'react-hot-toast';
import { serviceInstance } from '../../../lib/axios';
import useUser from '../../../hooks/useUser';
import DashboardLayout from '../../../components/layout/dashboardLayout';
import useLocationConfig from '../../../hooks/useLocationConfig';
import Spinner from '../../../components/loading';

const StatusDetails = () => {
  const navigate = useNavigate();
  const configData = useLocationConfig();
  const { id } = useParams();
  const [searchParams, _] = useSearchParams();
  const { user, selectedProfile } = useUser();

  const [activeMenuIndex, setActiveMenuIndex] = useState(null);
  const [webhooksList, setWebhooksList] = useState([]);
  const [flowsenseList, setFlowsenseList] = useState([]);
  const [selectedWebhookModal, setSelectedWebhookModal] = useState(null);

  const {
    data: details,
    mutate,
    isValidating,
  } = useSWR(
    user?.api_key && selectedProfile?.time_zone
      ? `/singleservice_status_initial`
      : null,
    async (key) => {
      const response = await serviceInstance.post(
        key,
        {
          id_cliente: selectedProfile?.id_cliente,
          timezone: selectedProfile?.time_zone,
          id_test: id,
        },
        {
          headers: {
            Authorization: user.api_key,
          },
        },
      );
      return {
        initialStatus: response.data.initial_status_data || [],
        webhookStatus: response.data.webhook_status_data || [],
        flowsenseStatus: response.data.flowsense_status_data || [],
      };
    },
  );

  useEffect(() => {
    mutate();
  }, []);

  useEffect(() => {
    if (details?.webhookStatus) {
      setWebhooksList(details.webhookStatus);
    }
    if (details?.flowsenseStatus) {
      setFlowsenseList(details.flowsenseStatus);
    }
  }, [details?.webhookStatus, details?.flowsenseStatus]);

  const initialStatusData = details?.initialStatus || [];

  // Conteo dinámico para FlowSense o Webhooks
  const activeList = flowsenseList.length > 0 ? flowsenseList : webhooksList;

  const correctCount = activeList.filter(
    (item) =>
      (item.estado_reportado || item.status) === 'normal' ||
      (item.estado_reportado || item.status) === 'correcto',
  ).length;

  const degradedCount = activeList.filter(
    (item) =>
      (item.estado_reportado || item.status) === 'degraded' ||
      (item.estado_reportado || item.status) === 'warning',
  ).length;

  const failedCount = activeList.filter(
    (item) =>
      (item.estado_reportado || item.status) === 'critical' ||
      (item.estado_reportado || item.status) === 'fallido',
  ).length;

  const hasFailed = failedCount > 0;
  const hasDegraded = degradedCount > 0;

  const configLists = (locationId) => {
    const config = [...new Set(configData)].find(
      (item) => item.id_ubicacion === locationId,
    );
    return config;
  };

  const detailsList = (locationId) => {
    const detail = initialStatusData?.filter(
      (item) => item.id_ubicacion === locationId,
    );
    return detail;
  };

  const uniqueDetails = initialStatusData.length > 0 && [
    ...new Map(
      initialStatusData.map((item) => [item.id_ubicacion, item]),
    ).values(),
  ];

  const currentStatusLabel = () => {
    if (hasFailed) return 'Fallido';
    if (hasDegraded) return 'Degradado';
    return 'Normal';
  };

  // Abrir Modal de Detalles
  const handleViewDetails = (webhook) => {
    setSelectedWebhookModal(webhook);
    setActiveMenuIndex(null);
  };

  const getContentType = () => {
    if (flowsenseList.length > 0) return 'flowsense';
    if (webhooksList.length > 0) return 'webhooks';
    if (uniqueDetails?.length > 0) return 'locations';
    return 'empty';
  };
  const contentType = getContentType();

  // Petición POST para Aceptar Webhook
  const handleAcceptWebhook = async (webhook, indexToUpdate) => {
    try {
      await serviceInstance.post(
        'https://api.realuptime.net/apo/webhook_object_accept',
        {
          id_cliente: selectedProfile?.id_cliente,
          id_test: parseInt(id, 10),
          object_id: webhook.object_reporting,
          mensaje: 'Se recupero',
        },
        {
          headers: {
            Authorization: user?.api_key,
          },
        },
      );

      setWebhooksList((prev) =>
        prev.map((item, index) =>
          index === indexToUpdate
            ? { ...item, estado_reportado: 'normal' }
            : item,
        ),
      );
      toast.success('Webhook aceptado correctamente');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error al aceptar webhook:', error);
      toast.error('Error al procesar la solicitud');
    }
    setActiveMenuIndex(null);
  };

  // Petición POST para Eliminar Webhook
  const handleDeleteWebhook = async (webhook, indexToDelete) => {
    try {
      await serviceInstance.post(
        'https://api.realuptime.net/apo/webhook_object_delete',
        {
          id_cliente: selectedProfile?.id_cliente,
          id_test: parseInt(id, 10),
          object_id: webhook.object_reporting,
        },
        {
          headers: {
            Authorization: user?.api_key,
          },
        },
      );

      setWebhooksList((prev) =>
        prev.filter((_, index) => index !== indexToDelete),
      );
      toast.success('Webhook eliminado correctamente');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error al eliminar webhook:', error);
      toast.error('Error al procesar la solicitud');
    }
    setActiveMenuIndex(null);
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-8">
        <div className="flex flex-row gap-2">
          <button
            className="flex flex-row items-center gap-2 text-white"
            onClick={() => navigate(-1)}
            tabIndex="0"
          >
            <RxChevronLeft className="size-6 text-4xl" />
          </button>
          <div className="text-white">
            <h1 className="text-3xl font-bold">{searchParams.get('name')}</h1>
            <span>Estado actual</span>
          </div>
        </div>
        <div className="flex items-center">
          <div
            className={classNames('p-2 min-h-12 min-w-12 max-w-12 max-h-12', {
              'bg-green-600':
                !hasFailed &&
                !hasDegraded &&
                parseInt(searchParams.get('status_code'), 10) === 0,
              'bg-yellow-400': hasDegraded,
              'bg-red-600':
                hasFailed ||
                parseInt(searchParams.get('status_code'), 10) === 2,
            })}
            style={{
              position: 'relative',
              overflow: 'hidden',
              width: '48px',
              height: '48px',
              clipPath:
                'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
            }}
          >
            <ReactSVG
              src={`/svgs/${+searchParams.get('monitor_family') === 1 ? 'robotBlack' : 'privalyticsBlack'}.svg`}
            />
            {searchParams.get('en_mantenimiento') && (
              <div className="absolute top-0 left-0">
                <ReactSVG src="svgs/mantenimiento.svg" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Panel de Resumen de Estados */}
      {activeList.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-white">
          <div
            className={classNames(
              'bg-gray-800 p-4 rounded-lg border-l-4 col-span-2 md:col-span-1',
              {
                'border-red-500': hasFailed,
                'border-yellow-500': !hasFailed && hasDegraded,
                'border-green-500': !hasFailed && !hasDegraded,
              },
            )}
          >
            <span className="text-gray-400 text-sm block">Estado actual</span>
            <span
              className={classNames('text-2xl font-bold', {
                'text-red-500': hasFailed,
                'text-yellow-400': !hasFailed && hasDegraded,
                'text-green-400': !hasFailed && !hasDegraded,
              })}
            >
              {currentStatusLabel()}
            </span>
          </div>

          <div className="bg-gray-800 p-4 rounded-lg flex flex-col justify-between">
            <span className="text-gray-400 text-sm">Correctos</span>
            <span className="text-xl font-bold text-green-400">
              {correctCount}
            </span>
          </div>

          <div className="bg-gray-800 p-4 rounded-lg flex flex-col justify-between">
            <span className="text-gray-400 text-sm">Degradados</span>
            <span className="text-xl font-bold text-yellow-400">
              {degradedCount}
            </span>
          </div>

          <div className="bg-gray-800 p-4 rounded-lg flex flex-col justify-between">
            <span className="text-gray-400 text-sm">Fallidos</span>
            <span className="text-xl font-bold text-red-400">
              {failedCount}
            </span>
          </div>
        </div>
      )}

      <div className="space-y-4 h-[70vh] overflow-y-auto">
        {!isValidating ? (
          <>
            {/* 1. Renderizado de FlowSense Status Data */}
            {contentType === 'flowsense' &&
              flowsenseList.map((item, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={classNames(
                    'bg-gray-800 w-full p-4 rounded-lg flex flex-col gap-2 border-l-4',
                    {
                      'border-red-500':
                        item.estado_reportado === 'critical' ||
                        item.estado_reportado === 'fallido',
                      'border-yellow-500':
                        item.estado_reportado === 'warning' ||
                        item.estado_reportado === 'degraded',
                      'border-green-500':
                        item.estado_reportado === 'normal' ||
                        item.estado_reportado === 'correcto',
                    },
                  )}
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-white text-xl font-bold">
                      {item.object_reporting}
                    </h3>
                    <span
                      className={classNames(
                        'px-2 py-1 rounded text-xs font-semibold uppercase text-white',
                        {
                          'bg-red-600':
                            item.estado_reportado === 'critical' ||
                            item.estado_reportado === 'fallido',
                          'bg-yellow-500':
                            item.estado_reportado === 'warning' ||
                            item.estado_reportado === 'degraded',
                          'bg-green-600':
                            item.estado_reportado === 'normal' ||
                            item.estado_reportado === 'correcto',
                        },
                      )}
                    >
                      {item.estado_reportado}
                    </span>
                  </div>
                  <p className="text-gray-300 text-sm">{item.status_message}</p>
                  <div className="text-gray-500 text-xs mt-1">
                    <span>Actualizado: {item.last_updated}</span>
                  </div>
                </div>
              ))}

            {contentType === 'webhooks' &&
              /* 2. Renderizado de Webhooks con opciones individuales y llamadas a API */
              webhooksList.map((webhook, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={classNames(
                    'bg-gray-800 w-full p-4 rounded-lg flex flex-col gap-2 border-l-4 relative',
                    {
                      'border-red-500': webhook.estado_reportado === 'critical',
                      'border-yellow-500':
                        webhook.estado_reportado === 'warning',
                      'border-green-500': webhook.estado_reportado === 'normal',
                    },
                  )}
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-white text-xl font-bold">
                      {webhook.object_reporting}
                    </h3>
                    <div className="flex items-center gap-3">
                      <span
                        className={classNames(
                          'px-2 py-1 rounded text-xs font-semibold uppercase',
                          {
                            'bg-red-600 text-white':
                              webhook.estado_reportado === 'critical',
                            'bg-yellow-400 text-white':
                              webhook.estado_reportado === 'warning',
                            'bg-green-600 text-white':
                              webhook.estado_reportado === 'normal',
                          },
                        )}
                      >
                        {webhook.estado_reportado}
                      </span>

                      <div className="relative">
                        <button
                          onClick={() =>
                            setActiveMenuIndex(
                              activeMenuIndex === index ? null : index,
                            )
                          }
                          className="text-gray-400 hover:text-white p-1 rounded-full focus:outline-none"
                        >
                          <FiMoreVertical className="size-5" />
                        </button>

                        {activeMenuIndex === index && (
                          <div className="absolute right-0 mt-2 w-44 bg-gray-700 rounded-lg shadow-xl z-50 border border-gray-600 overflow-hidden">
                            <button
                              onClick={() => handleViewDetails(webhook)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left text-white hover:bg-gray-600 transition-colors"
                            >
                              <FiInfo className="text-blue-400" /> Detalle
                            </button>
                            <button
                              onClick={() =>
                                handleAcceptWebhook(webhook, index)
                              }
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left text-white hover:bg-gray-600 transition-colors"
                            >
                              <FiCheckCircle className="text-green-400" />{' '}
                              Aceptar
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteWebhook(webhook, index)
                              }
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left text-red-400 hover:bg-gray-600 transition-colors"
                            >
                              <FiTrash2 /> Eliminar
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-300 text-sm">
                    {webhook.webhook_received_last_message}
                  </p>

                  <div className="flex justify-between items-center text-gray-500 text-xs mt-2">
                    <span>ID: {webhook.external_incident_id}</span>
                    <span>{webhook.date_received}</span>
                  </div>
                </div>
              ))}

            {contentType === 'locations' &&
              /* 3. Renderizado original para ubicaciones tradicionales */
              uniqueDetails.map((detail) => (
                <div
                  key={detail.timestamp}
                  role="button"
                  tabIndex="0"
                  onClick={() =>
                    navigate(
                      `/dashboard/status/${id}/incident-details?name=${configLists(detail.id_ubicacion)?.nombre}&id=${detail.id_ubicacion}&country=${configLists(detail.id_ubicacion)?.pais.toLowerCase()}&prev=${searchParams.get('name')}`,
                    )
                  }
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      event.currentTarget.click();
                    }
                  }}
                  className={classNames(
                    'bg-gray-800 p-4 rounded-lg flex justify-between items-center border-l-4 border-green-500',
                    {
                      'border-green-500':
                        detail.overall_location_status === 'ok',
                      'border-yellow-500':
                        detail.overall_location_status === 'degraded',
                      'border-red-500':
                        detail.overall_location_status === 'failed',
                    },
                  )}
                >
                  <div className="flex items-center space-x-3 w-full">
                    <div className="flex flex-col gap-2 w-full">
                      <p className="text-white text-2xl font-bold mb-2">
                        {configLists(detail.id_ubicacion)?.nombre}
                      </p>
                      {detailsList(detail.id_ubicacion)?.map(
                        (item, itemIndex) => (
                          <div
                            className="flex flex-row gap-2 text-gray-400 text-sm w-[15rem] truncate"
                            // eslint-disable-next-line react/no-array-index-key
                            key={itemIndex}
                          >
                            <ReactSVG
                              src={`/svgs/${item.step_success === 'true' ? 'check' : 'close'}.svg`}
                            />
                            <p>{item.label}</p>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              ))}

            {contentType === 'empty' && (
              <p className="text-white text-center">
                No hay información de estado disponible.
              </p>
            )}
          </>
        ) : (
          <Spinner />
        )}
      </div>

      {/* MODAL DE DETALLES DEL WEBHOOK */}
      {selectedWebhookModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
          <div className="bg-[#131B23] text-white w-full max-w-lg rounded-2xl p-6 relative shadow-2xl border border-gray-800">
            <button
              onClick={() => setSelectedWebhookModal(null)}
              className="absolute right-4 top-4 text-gray-400 hover:text-white p-2 rounded-full bg-gray-800 transition-colors"
            >
              <FiX className="size-5" />
            </button>
            <h2 className="text-2xl font-bold mb-4 pb-2 border-b border-gray-800">
              Detalle del Webhook
            </h2>
            <div className="space-y-4 text-sm">
              <div>
                <span className="text-gray-400 block text-xs uppercase tracking-wider">
                  Objeto Reportado
                </span>
                <span className="font-semibold text-lg text-white">
                  {selectedWebhookModal.object_reporting}
                </span>
              </div>
              <div>
                <span className="text-gray-400 block text-xs uppercase tracking-wider">
                  Estado Reportado
                </span>
                <span
                  className={classNames(
                    'inline-block px-2.5 py-1 rounded text-xs font-bold uppercase mt-1',
                    {
                      'bg-red-600 text-white':
                        selectedWebhookModal.estado_reportado === 'critical',
                      'bg-yellow-400 text-white':
                        selectedWebhookModal.estado_reportado === 'warning',
                      'bg-green-600 text-white':
                        selectedWebhookModal.estado_reportado === 'normal',
                    },
                  )}
                >
                  {selectedWebhookModal.estado_reportado}
                </span>
              </div>
              <div>
                <span className="text-gray-400 block text-xs uppercase tracking-wider">
                  ID de Incidente Externo
                </span>
                <span className="font-mono bg-gray-900 px-3 py-1.5 rounded block mt-1 text-gray-300 select-all">
                  {selectedWebhookModal.external_incident_id}
                </span>
              </div>
              <div>
                <span className="text-gray-400 block text-xs uppercase tracking-wider">
                  Mensaje del Webhook
                </span>
                <p className="bg-gray-900 p-3.5 rounded-xl mt-1 text-gray-200 leading-relaxed border border-gray-800">
                  {selectedWebhookModal.webhook_received_last_message}
                </p>
              </div>
              <div>
                <span className="text-gray-400 block text-xs uppercase tracking-wider">
                  Fecha de Recepción
                </span>
                <span className="text-gray-300">
                  {selectedWebhookModal.date_received}
                </span>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedWebhookModal(null)}
                className="bg-[#449670] hover:bg-[#367859] px-6 py-2.5 rounded-xl text-white font-semibold transition-colors shadow-lg"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default StatusDetails;
